import {
    CoreApplicationContext,
    CoreUser,
    GASettingKeys,
} from '@eventbrite/context-gen';
import { GenericOverlayOptions } from '@eventbrite/eds-structure';

export interface Collection {
    id: string;
    name: string;
    url: string;
    summary: string;
    imageUrl?: string;
    upcomingEventsCount: number;
}

export interface CollectionResponse {
    image: { url: string };
    event_count: { current_future_published: number };
    absolute_url: string;
    id: string;
    image_id: string;
    name: string;
    organization_id: string;
    organizer_id: string;
    relative_url: string;
    slug: string;
    status: string;
    summary: string;
}

export interface EventCollectionsResponse {
    collections: CollectionResponse[];
    pagination: { object_count: number; has_more_items: boolean };
}

export interface Event {
    name: string;
    id: string;
    url: string;
    ownerId?: string;
    source: string;
}

export interface Organizer {
    id: string;
    name: string;
    url: string;
    profilePicture: string;
    description?: string;
    eventTitle?: string;
    orgWebsite?: string;
    orgFacebook?: string;
    orgTwitter?: string;
}

export interface Location {
    latitude: number;
    longitude: number;
}
export interface User extends CoreUser {
    id?: number;
}

export interface Settings {
    baseUrl: string;
    isMobile: boolean;
    featureFlags?: {
        launchLinkToCreatorCollectionsOnListing?: boolean;
        enableOrganizerPanelComponentOnListings?: boolean;
    };
}

export interface GASettings extends GASettingKeys {
    category: string;
}

export interface ShareEventScopedProps {
    facebookOptions: {
        locale: string;
        version: string;
        page: string;
        xfbml: boolean;
        appId: string;
    };
    twitterHandle: string;
}

export interface FollowOrganizerScopedProps {
    isGDPRCountry: boolean;
}

export interface ContactOrganizerScopedProps {
    captchaKey: string;
}
export interface EventMapScopedProps {
    venueName: string;
    venueAddress: string;
    location: Location;
    gMapsApiKey: string;
}

export interface RemindMeScopedProps {
    isRemindedEvent: boolean;
}

export interface OverlayOptions extends GenericOverlayOptions {
    kind: 'modal' | 'dialog';
}

export interface ModalOptions extends OverlayOptions {
    children: React.ReactNode;
    type?: string;
}
export interface DialogOptions extends OverlayOptions {
    message: string;
}

export interface OverlayProps {
    options: ModalOptions | DialogOptions | undefined;
}

export interface NotificationOptions {
    children: React.ReactNode;
    hasCloseButton: boolean;
    isClosing: boolean;
    isNotificationSticky?: boolean;
    onClose?: () => void;
    position?: string;
    shouldPersist?: boolean;
    shouldStick: boolean;
    stickyTopHeight?: number;
}

export interface NotificationProps {
    fullWidth?: string;
    options: NotificationOptions | undefined;
}

export interface AppProps extends CoreApplicationContext {
    components: {
        shareEvent: ShareEventScopedProps;
        followOrganizer: FollowOrganizerScopedProps;
        contactOrganizer: ContactOrganizerScopedProps;
        eventMap: EventMapScopedProps;
        remindMe: RemindMeScopedProps;
    };
    event: Event;
    gaSettings: GASettings;
    settings: Settings;
    app_name: string;
    app_version: string;
    organizer: Organizer;
    user: User;
}

export interface ControlledProps extends AppProps {
    notificationOptions: NotificationOptions | undefined;
    overlayOptions: OverlayOptions | undefined;
    creatorCollections?: CollectionResponse[];
}

export type EnhancedAppProps = AppProps & {
    creatorCollections?: CollectionResponse[];
};

interface OrganizerData extends Organizer {
    followedByYou: boolean;
    numFollowers?: number;
    numUpcomingEvents?: number;
}
export interface FollowOrganizerProps {
    organizerId: string;
    gaSettings: GASettings;
    gaCategory: string;
    organizers: string[];
    isAuthenticated: boolean;
    userId: string;
    isGDPRCountry: boolean;
    size?: string;
    organizerData?: {
        [key: string]: OrganizerData;
    };
    renderFollowCount?(followCount: string): JSX.Element;
}

export type RemindMeProps = {
    eventUrl: string;
    eventId: string;
    eventName: string;
    isRemindedEvent: boolean;
    gaSettings: GASettings;
    organizerId: string;
};

export type OrganizerPanelListingsProps = {
    organizerName: string;
    organizerId: string;
    organizerProfilePicture: string;
    organizerUrl: string;
    organizerDescription?: string;
    organizerEventTitle?: string;
    captchaKey: string;
    isAuthenticated?: boolean;
    eventName: string;
    eventId: string;
    gaCategory: string;
    orgFacebook: string;
    orgTwitter: string;
    orgWebsite: string;
    enableOrganizerPanelComponentOnListings: boolean;
};

// GA tracking dropdown
export enum GACodes {
    GA_ACTION_COLLECTIONS_MODAL_LINK_CLICK = 'AllCollectionsModalLinkClick',
    GA_ACTION_COLLECTION_PAGE_LINK_CLICK = 'CollectionPageLinkClick',
    GA_ACTION_CONTACT_ORG_ATTEMPT_EVENT = 'ContactOrgAttempt',
    GA_ACTION_DIRECTIONS = 'Directions',
    GA_ACTION_MAP_INTERACTION = 'MapInteract',
    GA_ACTION_SHARE_EVENT = 'Share',
    GA_ACTION_REMINDME = 'RemindMeButton',
    GA_ACTION_UNREMINDME = 'TurnOffRemindMeButton',
    GA_CATEGORY_COLLECTION_CARD = 'collectioncard',
    GA_CATEGORY_CREATOR_COLLECTIONS_LISTING = 'creator-collections-listing',
    GA_CATEGORY_EVENT_CARD = 'eventcard',
    GA_CATEGORY_LISTING = 'listing',
    GA_LABEL_OPEN_MODAL = 'open-modal',
    GA_LABEL_SHELF = 'shelf',
    GA_LABEL_EVENT_DETAILS = 'event-details',
    GA_LABEL_BIKE = 'bicycling',
    GA_LABEL_BUS = 'transit',
    GA_LABEL_DRIVE = 'drive',
    GA_LABEL_WALK = 'walk',
    GA_LABEL_ZOOM = 'zoom-change',
}

// Affiliate codes
export enum AffiliateCodes {
    AFF_RELATED_EVENTS_LIVE = 'erellivmlt', // Related events for live source events
    AFF_RELATED_EVENTS_SAME_ORG = 'erelpanelorg', // Related events for the same organizer
    AFF_RELATED_EVENTS_SAME_COLLECTION = 'odclrlmeitc', // Related events in the same collection
    AFF_COLLECTIONS_FEATURING_THIS_EVENT = 'odclrlmctfte', // More collections featuring this event
}
